import { inject, Injectable, InjectionToken } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BaseParamsService } from '@app/core/base-params.service';
import { detect } from 'browser-langs';
import storage from 'local-storage-fallback';

const normalizeLanguage = (language: string): string =>
  ['no', 'nn'].includes(language) ? 'nb' : language;

const CHOSEN_LANGUAGE_STORAGE_KEY = 'language';

export const STORAGE_TOKEN = new InjectionToken<Storage | StorageFallback>(
  'STORAGE_TOKEN',
  {
    factory: (): typeof storage => storage,
  }
);

export const LANGUAGE_CODE_DETECTOR_TOKEN = new InjectionToken<() => string | null>(
  'LANGUAGE_DETECTOR_TOKEN',
  {
    factory: () => (): string | null => detect()?.code ?? null,
  }
);

@Injectable({
  providedIn: 'root',
})
export class LanguageSettingsService {
  private readonly translate = inject(TranslateService);
  private readonly baseParamsService = inject(BaseParamsService);
  private readonly storage = inject(STORAGE_TOKEN);
  private readonly languageCodeDetector = inject(LANGUAGE_CODE_DETECTOR_TOKEN);

  public setLanguage(language: string): void {
    this.baseParamsService.language = language;

    const normalizedLanguage = normalizeLanguage(language);

    this.translate.use(normalizedLanguage);
  }

  public getLanguage({
                       availableLanguages,
                       defaultLanguage,
                     }: {
    availableLanguages: string[];
    defaultLanguage: string;
  }): string {
    const paramsLanguage = this.baseParamsService.paramsLanguage;
    if (paramsLanguage) {
      return paramsLanguage;
    }

    const oidcLanguages = this.baseParamsService.oidcLanguages;
    if (oidcLanguages?.length) {
      for (const lang of oidcLanguages) {
        if (availableLanguages.includes(lang)) {
          return lang;
        }
      }
    }
    const storedLanguage = this.getStoredLanguage();
    if (storedLanguage && this.storedLanguageEnable(availableLanguages)) {
      return storedLanguage;
    }

    return this.getBrowserPreferedLanguage(availableLanguages) || defaultLanguage;
  }

  public saveChosenLanguage(language: string): void {
    this.storage.setItem(CHOSEN_LANGUAGE_STORAGE_KEY, language);
  }

  public getStoredLanguage(): string | null {
    return this.storage.getItem(CHOSEN_LANGUAGE_STORAGE_KEY);
  }

  private getBrowserPreferedLanguage(languages: string[]): string | null {
    const detectedLanguageCode = this.languageCodeDetector();
    if (!detectedLanguageCode) {
      return null;
    }

    if (languages.includes(detectedLanguageCode)) {
      return detectedLanguageCode;
    }

    const normalizedLanguageCode = normalizeLanguage(detectedLanguageCode);
    if (languages.includes(normalizedLanguageCode)) {
      return normalizedLanguageCode;
    }

    const foundLanguage = languages.find((language) => normalizeLanguage(language) === normalizedLanguageCode);
    return foundLanguage ?? null;
  }

  private storedLanguageEnable(languages: string[]): boolean {
    return languages.some(lang => lang === this.getStoredLanguage());
  }
}
